import type { ApiClient } from './index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { authenticatedApiClient } from './axios'
import {
  NOTIFICATIONS_ENDPOINT,
  NOTIFICATIONS_MARK_ALL_READ_ENDPOINT,
  NOTIFICATIONS_MARK_READ_ENDPOINT,
  NOTIFICATION_TYPES_ENDPOINT,
  USER_UPDATE_ENDPOINT,
} from './config/api'
import type { Notification, NotificationFetchParams, NotificationType } from '@cobu/types'

// Legacy-compatible helpers used by apps that pass a custom ApiClient
export async function getNotificationTypes(api: ApiClient): Promise<NotificationType[]> {
  const res = await api.http.get<NotificationType[]>(NOTIFICATION_TYPES_ENDPOINT)
  return (res.data as any) ?? []
}

export async function setDisabledPushTypes(api: ApiClient, userId: string, disabled: string[]) {
  await api.http.patch(`${USER_UPDATE_ENDPOINT}/${userId}`, { disabledPushNotificationTypes: disabled })
}

// Service-style functions using the package's authenticated client
export async function getNotifications(userUuid: string, params: NotificationFetchParams = {}): Promise<Notification[]> {
  const { page = 1 } = params
  return await authenticatedApiClient.get<Notification[]>(NOTIFICATIONS_ENDPOINT, { params: { userUuid, page } })
}

export async function markAllNotificationsAsRead(userUuid: string): Promise<void> {
  await authenticatedApiClient.post(NOTIFICATIONS_MARK_ALL_READ_ENDPOINT, { userUuid })
}

export async function markNotificationAsRead(notificationUuid: string): Promise<void> {
  await authenticatedApiClient.patch(`${NOTIFICATIONS_MARK_READ_ENDPOINT}/${notificationUuid}`)
}

// React Query hooks (kept in this file for convenience, similar to groups hooks split)
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (userUuid: string, params: NotificationFetchParams) => [...notificationKeys.lists(), userUuid, params] as const,
}

export function useGetNotificationsQuery(userUuid: string, params: NotificationFetchParams = {}) {
  return useQuery<Notification[], AxiosError>({
    queryKey: notificationKeys.list(userUuid, params),
    queryFn: () => getNotifications(userUuid, params),
    enabled: !!userUuid,
    staleTime: 0,
  })
}

export function useMarkAllAsReadMutation() {
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError, string>({
    mutationFn: (userUuid: string) => markAllNotificationsAsRead(userUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export function useMarkAsReadMutation() {
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError, string>({
    mutationFn: (notificationUuid: string) => markNotificationAsRead(notificationUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}
